import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import loader from "../../../../methods/loader";
import moment from "moment";
import ApiClient from "../../../../methods/api/apiClient";
import Checkbox from "../../../../components/checkbox/checkbox";

const QATab = ({ chatbotData }) => {
  // ── Form state ──
  const [items, setItems] = useState([
    { id: Date.now(), subject: "", question: "", answer: "" },
  ]);
  const [errors, setErrors] = useState([
    { subject: "", question: "", answer: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [qaEditId, setQaEditId] = useState(null);

  // ── List state ──
  const [responselist, setresponselist] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchData = async () => {
    const res = await ApiClient.get("qa/listing");
    setresponselist(res?.data?.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Form helpers ──
  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { id: Date.now(), subject: "", question: "", answer: "" },
    ]);
    setErrors((prev) => [...prev, { subject: "", question: "", answer: "" }]);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
    setErrors((prev) =>
      prev.map((err, i) =>
        i === index
          ? {
              ...err,
              [field]: value.trim() === "" ? `${field} cannot be blank.` : "",
            }
          : err,
      ),
    );
  };

  const validate = () => {
    const newErrors = items.map((item) => ({
      subject: item.subject.trim() === "" ? "Subject cannot be blank." : "",
      question: item.question.trim() === "" ? "Question cannot be blank." : "",
      answer: item.answer.trim() === "" ? "Answer cannot be blank." : "",
    }));
    setErrors(newErrors);
    return newErrors.every((e) => !e.subject && !e.question && !e.answer);
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      swal({
        text: "No items to submit.",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    if (!validate()) return;

    setIsSubmitting(true);
    loader(true);
    try {
      if (qaEditId) {
        // Edit mode — single item
        const item = items[0];
        await ApiClient.put("qa/edit", {
          id: qaEditId,
          subject: item.subject,
          question: item.question,
          answer: item.answer,
        });
        swal({
          text: "Q&A updated successfully",
          icon: "success",
          className: "smallModal",
        });
      } else {
        // Add mode — batch
        await ApiClient.post("qa/add", {
          counts: items.map((item) => ({
            subject: item.subject,
            question: item.question,
            answer: item.answer,
          })),
        });
        swal({
          text: "Q&A added successfully",
          icon: "success",
          className: "smallModal",
        });
      }
      await fetchData();
      resetForm();
    } catch {
      swal({
        text: "Something went wrong. Please try again.",
        icon: "error",
        className: "smallModal",
      });
    } finally {
      setIsSubmitting(false);
      loader(false);
    }
  };

  const resetForm = () => {
    setItems([{ id: Date.now(), subject: "", question: "", answer: "" }]);
    setErrors([{ subject: "", question: "", answer: "" }]);
    setQaEditId(null);
  };

  const editButtonClick = async (item) => {
    const res = await ApiClient.get(`qa/detail?id=${item.id}`);
    const data = res?.data || res;
    setQaEditId(data.id || item.id);
    setItems([
      {
        id: data.id || item.id,
        subject: data.subject || data.text_heading || "",
        question: data.question || "",
        answer: data.answer || "",
      },
    ]);
    setErrors([{ subject: "", question: "", answer: "" }]);
    try {
      document
        .getElementById("qa-form-top")
        ?.scrollIntoView({ behavior: "smooth" });
    } catch {}
  };

  // ── Table selection ──
  const handleSelectAll = () => {
    if (!selectAll) {
      setSelectedIds(responselist.map((item) => item.id));
      setSelectedRows(responselist.map((_, i) => i));
    } else {
      setSelectedIds([]);
      setSelectedRows([]);
    }
    setSelectAll(!selectAll);
  };

  const handleRowSelect = (index, id) => {
    if (selectedRows.includes(index)) {
      setSelectedRows((prev) => prev.filter((r) => r !== index));
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      setSelectAll(false);
    } else {
      const newRows = [...selectedRows, index];
      setSelectedRows(newRows);
      setSelectedIds((prev) => [...prev, id]);
      if (newRows.length === responselist.length) setSelectAll(true);
    }
  };

  const DeleteOneData = async (id) => {
    loader(true);
    await ApiClient.delete(`qa/delete?id=${id}`);
    await fetchData();
    setSelectedIds([]);
    setSelectedRows([]);
    swal({
      text: "Q&A deleted successfully",
      icon: "success",
      className: "smallModal",
    });
    loader(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      swal({
        text: "Please select items first.",
        icon: "error",
        className: "smallModal",
      });
      return;
    }
    const confirm = await swal({
      text: `Delete ${selectedIds.length} item(s)?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    loader(true);
    for (const id of selectedIds) {
      await ApiClient.delete(`qa/delete?id=${id}`);
    }
    await fetchData();
    setSelectedIds([]);
    setSelectedRows([]);
    setSelectAll(false);
    swal({
      text: "Selected Q&As deleted.",
      icon: "success",
      className: "smallModal",
    });
    loader(false);
  };

  const hasContent = items.some((i) => i.subject || i.question || i.answer);

  return (
    <div className="w-full border border-t-0 rounded-b-2xl bg-white p-5 lg:p-8 space-y-8">
      {/* ── Form Section ── */}
      <div className="space-y-4" id="qa-form-top">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-[#115b9d29] flex items-center justify-center flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#115B9D]"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <div>
              <h3 className="text-md sm:text-lg text-gray-900">
                {qaEditId ? "Edit Q&A" : "Q&A"}
              </h3>
              {qaEditId && (
                <p className="text-xs text-amber-600 font-medium">
                  Edit mode — retrain chatbot to save changes.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {qaEditId ? (
              <button
                onClick={resetForm}
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100"
              >
                Cancel Edit
              </button>
            ) : items?.length > 1 ? (
              <button
                onClick={() => {
                  setItems([]);
                  setErrors([]);
                }}
                type="button"
                className="text-sm text-red-500 hover:text-red-600 transition-colors font-medium"
              >
                Clear all
              </button>
            ) : null}
            {!qaEditId && (
              <button
                onClick={addItem}
                type="button"
                className="size-9 rounded-xl bg-white border border-gray-300 hover:border-[#115B9D] hover:bg-[#115b9d29] flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                title="Add Q&A pair"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Q&A Cards */}
        <div className="space-y-4 max-h-[800px] overflow-auto no-scrollbar pr-1">
          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-2xl">
              <div className="size-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-600"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <path d="M12 17h.01" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                No Q&A pairs added yet
              </p>
              <button
                onClick={addItem}
                type="button"
                className="px-4 py-2 bg-[#115B9D] text-white text-sm rounded-xl hover:bg-opacity-90 transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
                Add Q&A pair
              </button>
            </div>
          )}

          {items.map((item, index) => (
            <div
              key={item.id}
              className="p-5 bg-white border border-gray-200 rounded-2xl space-y-4 shadow-sm"
            >
              {/* Card header */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#115B9D] bg-[#115B9D17] px-2.5 py-1 rounded-full">
                  {qaEditId ? "Editing" : `Pair ${index + 1}`}
                </span>
                {!qaEditId && items?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="size-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      <line x1="10" x2="10" y1="11" y2="17" />
                      <line x1="14" x2="14" y1="11" y2="17" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <label className="block text-sm text-gray-700">Subject</label>
                <input
                  type="text"
                  value={item.subject}
                  onChange={(e) =>
                    handleFieldChange(index, "subject", e.target.value)
                  }
                  placeholder="e.g. JavaScript, Billing, Support…"
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#115B9D]/20 outline-none transition-all ${
                    errors[index]?.subject
                      ? "border-red-400 focus:border-red-400"
                      : "border-gray-300 focus:border-[#115B9D]"
                  }`}
                />
                {errors[index]?.subject && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                    {errors[index].subject}
                  </p>
                )}
              </div>

              {/* Question */}
              <div className="space-y-1.5">
                <label className="block text-sm text-gray-700">Question</label>
                <textarea
                  value={item.question}
                  onChange={(e) =>
                    handleFieldChange(index, "question", e.target.value)
                  }
                  placeholder="How does it work?"
                  rows={3}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#115B9D]/20 outline-none transition-all resize-none ${
                    errors[index]?.question
                      ? "border-red-400 focus:border-red-400"
                      : "border-gray-300 focus:border-[#115B9D]"
                  }`}
                />
                {errors[index]?.question && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                    {errors[index].question}
                  </p>
                )}
              </div>

              {/* Answer */}
              <div className="space-y-1.5">
                <label className="block text-sm text-gray-700">Answer</label>
                <textarea
                  value={item.answer}
                  onChange={(e) =>
                    handleFieldChange(index, "answer", e.target.value)
                  }
                  placeholder="Provide a clear and helpful answer…"
                  rows={4}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:ring-2 focus:ring-[#115B9D]/20 outline-none transition-all resize-none ${
                    errors[index]?.answer
                      ? "border-red-400 focus:border-red-400"
                      : "border-gray-300 focus:border-[#115B9D]"
                  }`}
                />
                {errors[index]?.answer && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" x2="12" y1="8" y2="12" />
                      <line x1="12" x2="12.01" y1="16" y2="16" />
                    </svg>
                    {errors[index].answer}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Submit */}
        {items.length > 0 && (
          <div className="flex justify-center pt-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasContent}
              type="button"
              className="px-10 py-3 bg-[#115B9D] text-white rounded-xl hover:bg-opacity-90 transition-colors shadow-sm hover:shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  {qaEditId ? "Updating…" : "Submitting…"}
                </>
              ) : qaEditId ? (
                "Update Q&A"
              ) : (
                "Submit"
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Already Added Q&As ── */}
      <div className="space-y-4">
        <h3 className="text-md sm:text-lg text-gray-900">
          Already Added Q&amp;As
        </h3>

        {/* ================= Desktop Table ================= */}
        <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-5 w-12">
                    <Checkbox onChange={handleSelectAll} checked={selectAll} />
                  </th>

                  <th className="text-left py-3 px-5">
                    <span className="text-sm text-gray-700 font-normal">
                      Subject
                    </span>
                  </th>

                  <th className="text-left py-3 px-5">
                    <span className="text-sm text-gray-700 font-normal">
                      Upload Date
                    </span>
                  </th>

                  <th className="text-left py-3 px-5">
                    <span className="text-sm text-gray-700 font-normal">
                      Question
                    </span>
                  </th>

                  <th className="text-left py-3 px-5">
                    <span className="text-sm text-gray-700 font-normal">
                      Status
                    </span>
                  </th>

                  <th className="text-left py-3 px-5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-700 font-normal">
                        Action
                      </span>

                      {selectedIds.length > 0 && (
                        <button
                          type="button"
                          onClick={handleDeleteSelected}
                          className="text-xs text-red-500 hover:text-red-600 font-medium transition-colors whitespace-nowrap"
                        >
                          Delete ({selectedIds.length})
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {responselist.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="size-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                            <path d="M12 17h.01" />
                          </svg>
                        </div>

                        <p className="text-sm text-gray-600">
                          No Q&As added yet
                        </p>

                        <p className="text-xs text-gray-300">
                          Add Q&A pairs above to get started
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  responselist.map((item, index) => (
                    <tr
                      key={index}
                      className="border-t border-gray-100 first:border-t-0"
                    >
                      <td className="py-3 px-5">
                        <Checkbox
                          onChange={() => handleRowSelect(index, item.id)}
                          checked={selectedRows.includes(index)}
                        />
                      </td>

                      <td className="py-3 px-5">
                        <p className="text-gray-900 flex items-center gap-2 whitespace-nowrap text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600 flex-shrink-0"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                            <path d="M12 17h.01" />
                          </svg>

                          {item?.text_heading || item?.subject || "—"}
                        </p>
                      </td>

                      <td className="py-3 px-5">
                        <p className="text-gray-900 text-sm whitespace-nowrap">
                          {moment(item?.created_at || item?.createdAt).format(
                            "YYYY-MM-DD HH:mm",
                          )}
                        </p>
                      </td>

                      <td className="py-3 px-5">
                        <p className="text-gray-900 text-sm whitespace-nowrap">
                          {item?.question || "—"}
                        </p>
                      </td>

                      <td className="py-3 px-5">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Success
                        </span>
                      </td>

                      <td className="py-3 px-5">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => editButtonClick(item)}
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                            title="Edit"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => DeleteOneData(item?.id)}
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                            title="Delete"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" x2="10" y1="11" y2="17" />
                              <line x1="14" x2="14" y1="11" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= Mobile Cards ================= */}
        <div className="block md:hidden space-y-4">
          {responselist.length === 0 ? (
            <div className="border border-gray-200 rounded-xl bg-white p-8">
              <div className="flex flex-col items-center gap-2">
                <div className="size-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <path d="M12 17h.01" />
                  </svg>
                </div>

                <p className="text-sm text-gray-600">No Q&As added yet</p>
                <p className="text-xs text-gray-300">
                  Add Q&A pairs above to get started
                </p>
              </div>
            </div>
          ) : (
            <>
              {selectedIds.length > 0 && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    className="px-4 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition"
                  >
                    Delete Selected ({selectedIds.length})
                  </button>
                </div>
              )}

              {responselist.map((item, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl bg-white shadow-sm p-4 space-y-4"
                >
                  {/* Top */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Checkbox
                        onChange={() => handleRowSelect(index, item.id)}
                        checked={selectedRows.includes(index)}
                      />

                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 mb-1">Subject</p>

                        <p className="font-medium text-gray-900 break-words">
                          {item?.text_heading || item?.subject || "—"}
                        </p>
                      </div>
                    </div>

                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#f0fdf4] text-[#008236] text-xs font-medium whitespace-nowrap">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      Success
                    </span>
                  </div>

                  {/* Upload Date */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Upload Date</p>

                    <p className="text-sm text-gray-900">
                      {moment(item?.created_at || item?.createdAt).format(
                        "YYYY-MM-DD HH:mm",
                      )}
                    </p>
                  </div>

                  {/* Question */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Question</p>

                    <p className="text-sm text-gray-900 break-words">
                      {item?.question || "—"}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => editButtonClick(item)}
                      className="h-10 w-10 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                      title="Edit"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => DeleteOneData(item?.id)}
                      className="h-10 w-10 rounded-lg border border-gray-200 hover:bg-red-50 flex items-center justify-center text-gray-600 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        <line x1="10" x2="10" y1="11" y2="17" />
                        <line x1="14" x2="14" y1="11" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QATab;
